import React from 'react';
import { observer } from 'mobx-react';

import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { withToast } from 'core/components/toast';
import { useStore } from 'core/store/store.mobx';
import { OfferPageTabs } from 'core/store/offer-store';
import useSession from 'core/hooks/use-session';
import { track } from 'core/helpers/analytics';
import pencilIcon from 'core/assets/svg/pencil-white.svg';

import { differenceInCalendarDays, parseISO } from 'date-fns';
import { useFormikContext } from 'formik';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';

import useStyles from '../footer.styles';

const AQDOfferFooter = observer(({ onUpdate }) => {
  const { offer: store } = useStore();
  const { isAgency, user } = useSession();
  const { values } = useFormikContext();
  const classes = useStyles();

  // Safety check: don't render if critical data is missing
  if (!store?.offer || !store.selectedOption) {
    return null;
  }

  const handleTabSwitch = () => {
    store.setScrollTo('autoSettings');
    store.setCurrentTab(OfferPageTabs.SETTINGS);
  };

  const handleOnUpdate = () => {
    onUpdate();

    // Get the AQD discount amount from the selected option
    const quoteFriendlySelectedOption = getQuoteFriendlySelectedOption(store.selectedOption);
    const selectedOptionData = store.offer?.options?.find((opt) => opt.type === quoteFriendlySelectedOption);
    const futureEffectiveSavings =
      selectedOptionData?.autoCoverages?.find((coverage) => coverage.type === 'future_effective_date_savings')
        ?.amount || 0;

    const creationDate = store.offer?.initialCreationDateTime ? parseISO(store.offer.initialCreationDateTime) : null;
    const autoEffectiveDate = store.offer?.quote?.global?.autoEffectiveDate
      ? parseISO(values?.global?.autoEffectiveDate)
      : null;
    const eligibleForAQDTracking =
      creationDate && autoEffectiveDate && differenceInCalendarDays(autoEffectiveDate, creationDate) >= 3;

    if (eligibleForAQDTracking && futureEffectiveSavings > 0) {
      track('Staff - Advanced Quote discount applied', {
        offerId: store.offer?.id,
        staffUsername: user?.username,
        isAgency,
        webUserId: store.offer?.webUserId,
        offerType: store.selectedOption,
        state: store.state,
        amountSaved: futureEffectiveSavings
      });
    }
  };

  return (
    <>
      <div className={classes.titleContainer}>
        <Label className={classes.title} type="subtitle">
          The offer’s effective date(s) are in the past.
        </Label>
        <Label className={classes.subtitle} type="subtitle">
          Please update the effective date(s) to be in the future.
        </Label>
      </div>
      <div>
        <Button
          onClick={handleTabSwitch}
          disabled={store.loading}
          loading={store.loading}
          variant="contained"
          color="secondary"
          className={classes.btn}
        >
          <img src={pencilIcon} alt="Orange pencil icon" className={classes.editIcon} />
          <Label className={classes.btnLabel}>Update effective date(s)</Label>
        </Button>
        <Button
          onClick={handleOnUpdate}
          disabled={store.loading}
          loading={store.loading}
          variant="contained"
          color="primary"
          className={classes.aqdUpdateButton}
        >
          <Label className={classes.btnLabel}>Update offer price</Label>
        </Button>
      </div>
    </>
  );
});
export default withToast(AQDOfferFooter);
